// API client for tenant-related endpoints.

export interface TenantAuthConfig {
  tenant_id: string
  auth_mode: 'oidc' | 'magic_link'
  // OIDC-only fields — only present when auth_mode === 'oidc'
  issuer_url?: string
  client_id?: string
}

/**
 * Read the tenant signature injected by the Go server into the HTML.
 * Returns null if not present (e.g., on the landing page).
 */
function getTenantSignature(): string | null {
  const meta = document.querySelector('meta[name="tenant-sig"]')
  return meta?.getAttribute('content') ?? null
}

/**
 * Fetch the OIDC auth configuration for a tenant by slug.
 * Includes the server-injected signature to prevent enumeration.
 */
export async function fetchTenantAuthConfig(slug: string): Promise<TenantAuthConfig> {
  const sig = getTenantSignature()

  const headers: Record<string, string> = {}
  if (sig) {
    headers['X-Lookup-Sig'] = sig
  }

  const response = await fetch(`/t/${encodeURIComponent(slug)}/auth-config`, { headers })

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Tenant not found')
    }
    throw new Error(`Failed to fetch tenant auth config: ${response.status}`)
  }

  return response.json() as Promise<TenantAuthConfig>
}
